
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Search, RefreshCw, Plus, Edit, Trash2, Eye, Type, Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { adminService } from '@/services/admin';
import WysiwygEditor from '@/components/WysiwygEditor';

const guideSchema = z.object({
  title: z.string().min(1, '標題不能為空'),
  summary: z.string().min(1, '摘要不能為空'),
  content: z.string().min(1, '內容不能為空'),
  tags: z.array(z.string()).optional(),
  cover_image_url: z.string().optional(),
});

type GuideFormData = z.infer<typeof guideSchema>;

interface Guide {
  id: string;
  title: string;
  summary: string;
  content: string;
  tags?: string[];
  cover_image_url?: string;
  created_at: string;
  updated_at?: string;
}

const AdminGuides = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [filteredGuides, setFilteredGuides] = useState<Guide[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);
  const [viewingGuide, setViewingGuide] = useState<Guide | null>(null);
  const [textSize, setTextSize] = useState(16);
  const [deletingGuide, setDeletingGuide] = useState<Guide | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const form = useForm<GuideFormData>({
    resolver: zodResolver(guideSchema),
    defaultValues: {
      title: '',
      summary: '',
      content: '',
      tags: [],
      cover_image_url: '',
    },
  });

  useEffect(() => {
    fetchGuides();
  }, []);

  useEffect(() => {
    let filtered = guides;
    if (searchTerm) {
      filtered = filtered.filter(guide =>
        guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.summary.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredGuides(filtered);
  }, [guides, searchTerm]);

  const fetchGuides = async () => {
    try {
      setIsLoading(true);
      console.log('=== 開始取得指南資料 ===');
      console.log('使用 adminService.getGuides()...');
      
      const data = await adminService.getGuides();
      console.log('取得的指南數量:', data?.length || 0);
      console.log('指南資料:', data);
      
      // 特別檢查是否有加拿大租屋攻略這篇文章
      const canadaGuide = data?.find(guide => 
        guide.title.includes('加拿大租屋攻略') || 
        guide.title.includes('新手必讀')
      );
      
      if (canadaGuide) {
        console.log('🚨 發現加拿大租屋攻略文章:', canadaGuide);
        console.log('文章ID:', canadaGuide.id);
        console.log('創建時間:', canadaGuide.created_at);
      } else {
        console.log('✅ 未發現加拿大租屋攻略文章');
      }
      
      setGuides(data || []);
    } catch (error: any) {
      console.error('取得指南時發生錯誤:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: error?.message || '無法載入指南',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingGuide(null);
    form.reset({
      title: '',
      summary: '',
      content: '',
      tags: [],
      cover_image_url: '',
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (guide: Guide) => {
    setEditingGuide(guide);
    form.reset({
      title: guide.title,
      summary: guide.summary,
      content: guide.content,
      tags: guide.tags || [],
      cover_image_url: guide.cover_image_url || '',
    });
    setIsDialogOpen(true);
  };

  const handleView = (guide: Guide) => {
    setViewingGuide(guide);
    setIsViewDialogOpen(true);
  };

  const handleDeleteClick = (guide: Guide) => {
    console.log('準備刪除指南:', guide.title, 'ID:', guide.id);
    setDeletingGuide(guide);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = async (data: GuideFormData) => {
    try {
      setIsSaving(true);
      
      // Ensure all required fields are present for the admin service
      const guideData = {
        title: data.title,
        summary: data.summary,
        content: data.content,
        tags: data.tags || [],
        cover_image_url: data.cover_image_url || '',
      };
      
      if (editingGuide) {
        await adminService.updateGuide(editingGuide.id, guideData);
        toast({
          title: '更新成功',
          description: '指南已更新',
        });
      } else {
        await adminService.createGuide(guideData);
        toast({
          title: '創建成功',
          description: '指南已創建',
        });
      }

      setIsDialogOpen(false);
      setEditingGuide(null);
      form.reset();
      fetchGuides();
    } catch (error: any) {
      console.error('Error saving guide:', error);
      toast({
        variant: 'destructive',
        title: editingGuide ? '更新失敗' : '創建失敗',
        description: error?.message || '無法保存指南',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingGuide) return;

    try {
      console.log('=== 開始刪除指南 ===');
      console.log('指南標題:', deletingGuide.title);
      console.log('指南ID:', deletingGuide.id);
      
      await adminService.deleteGuide(deletingGuide.id);
      console.log('✅ 刪除成功');

      toast({
        title: '刪除成功',
        description: `指南「${deletingGuide.title}」已成功刪除`,
      });
      
      setDeletingGuide(null);
      setIsDeleteDialogOpen(false);
      
      console.log('重新載入指南列表...');
      await fetchGuides();
      
    } catch (error: any) {
      console.error('🚨 刪除指南時發生錯誤:', error);
      toast({
        variant: 'destructive',
        title: '刪除失敗',
        description: error?.message || '發生未知錯誤',
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeletingGuide(null);
    setIsDeleteDialogOpen(false);
  };

  // 強制重新載入並清除快取
  const forceRefresh = async () => {
    console.log('=== 強制重新載入 ===');
    setGuides([]); // 先清空列表
    await fetchGuides();
    window.location.reload(); // 重新載入頁面以清除所有快取
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-2 text-gray-600">載入指南中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">指南管理</h1>
          <p className="text-gray-600 dark:text-gray-400">管理租屋指南內容</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            新增指南
          </Button>
          <Button onClick={fetchGuides} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            重新載入
          </Button>
          <Button onClick={forceRefresh} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            強制刷新
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>指南列表 ({filteredGuides.length} 筆)</CardTitle>
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="搜尋指南..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredGuides.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">暫無指南</h3>
              <p className="text-gray-600">目前還沒有任何指南</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>標題</TableHead>
                  <TableHead>摘要</TableHead>
                  <TableHead>標籤</TableHead>
                  <TableHead>創建時間</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGuides.map((guide) => (
                  <TableRow key={guide.id}>
                    <TableCell>
                      <div className="font-medium">{guide.title}</div>
                      {guide.title.includes('加拿大租屋攻略') && (
                        <div className="text-xs text-red-600 mt-1">🚨 這是要刪除的文章</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-gray-600">
                        {guide.summary}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {guide.tags?.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                        {guide.tags && guide.tags.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{guide.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(guide.created_at).toLocaleDateString('zh-TW')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(guide)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(guide)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteClick(guide)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 編輯/新增對話框 */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingGuide ? '編輯指南' : '新增指南'}
              {isSaving && <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>標題</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="輸入指南標題..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="summary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>摘要</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={4} placeholder="輸入指南摘要..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cover_image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>封面圖片 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>內容</FormLabel>
                        <FormControl>
                          <WysiwygEditor 
                            value={field.value} 
                            onChange={field.onChange}
                            placeholder="開始撰寫指南內容..."
                            rows={20}
                            showFontSizeControls={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isSaving}
                >
                  取消
                </Button>
                <Button type="submit" disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? '保存中...' : (editingGuide ? '更新' : '創建')}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* 預覽對話框 */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>預覽指南</DialogTitle>
              <div className="flex items-center space-x-2">
                <Type className="h-4 w-4" />
                <span className="text-sm">文字大小:</span>
                <div className="w-24">
                  <Slider
                    value={[textSize]}
                    onValueChange={(value) => setTextSize(value[0])}
                    min={12}
                    max={24}
                    step={1}
                  />
                </div>
                <span className="text-sm w-8">{textSize}px</span>
              </div>
            </div>
          </DialogHeader>
          {viewingGuide && (
            <div className="space-y-4" style={{ fontSize: `${textSize}px` }}>
              <div>
                <h2 className="text-2xl font-bold mb-2">{viewingGuide.title}</h2>
                <p className="text-gray-600 mb-4">{viewingGuide.summary}</p>
              </div>
              {viewingGuide.cover_image_url && (
                <img
                  src={viewingGuide.cover_image_url}
                  alt={viewingGuide.title}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: viewingGuide.content }}
              />
              {viewingGuide.tags && viewingGuide.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {viewingGuide.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 刪除確認對話框 */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除指南「{deletingGuide?.title}」嗎？此操作無法恢復。
              {deletingGuide?.title.includes('加拿大租屋攻略') && (
                <div className="mt-2 text-red-600 font-medium">
                  ⚠️ 這是您要求刪除的加拿大租屋攻略文章
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminGuides;
